import { useCallback, useState } from "react";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { useOperationsStore } from "./operationsStore";
import {
  Operation,
  OPERATION_SOURCE_TYPE,
  UNIT_COST_CALCULATION,
  CSV_IMPORT_TYPE,
} from "@forgepoint/shared";
import { Badge } from "@/components/ui/badge";
import { Trash2, Loader, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DataTableBadge } from "@/components/ui/data-table-badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useEntitySearch } from "@/lib/useEntitySearch";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { toast } from "sonner";
import OperationEditorSheet from "./OperationEditorSheet";
import CsvImportSheet from "@/modules/csvImports/CsvImportSheet";
import { useHasPermission } from "@/store/authStore";
import { Permission } from "@forgepoint/shared";
import { OperationRowPrice } from "@/components/operationSelector/OperationRowPrice";

export default function OperationsController() {
  const canEditOperations = useHasPermission(Permission.OPERATIONS_EDIT);
  const {
    operations,
    listLoading,
    listError,
    operationLoadingStates,
    fetchOperations,
    deleteOperation,
  } = useOperationsStore();

  const [showArchived, setShowArchived] = useState(false);

  const {
    entitiesArray: operationsArray,
    handleSearch,
    setDisplayedEntityIds: setDisplayedOperationIds,
  } = useEntitySearch({
    entities: operations,
    fetchEntities: (params) =>
      fetchOperations({
        search: params?.search,
        includeArchived: showArchived,
      }).then((result) => result.ids),
  });

  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isImportSheetOpen, setIsImportSheetOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedOperationId, setSelectedOperationId] = useState<string | null>(
    null,
  );
  const [operationToDelete, setOperationToDelete] = useState<Operation | null>(
    null,
  );

  const columns: DataTableColumn<Operation>[] = [
    {
      key: "name",
      header: "Name",
      type: "string",
      format: (_value: unknown, operation: Operation) => (
        <div className="flex items-center gap-2">
          <span>{operation.name}</span>
          {operation.archivedAt && (
            <DataTableBadge color="amber">Archived</DataTableBadge>
          )}
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      type: "string",
      className: "hidden md:table-cell",
    },
    {
      key: "operationSourceType",
      header: "Type",
      width: 100,
      className: "hidden md:table-cell",
      format: (_value: unknown, operation: Operation) => (
        <Badge variant="outline" className="text-xs">
          {operation.operationSourceType === OPERATION_SOURCE_TYPE.OUTSIDE
            ? "Outside"
            : "Inside"}
        </Badge>
      ),
    },
    {
      key: "unitCostCalculation",
      header: "Costing",
      width: 100,
      className: "hidden md:table-cell",
      format: (_value: unknown, operation: Operation) =>
        operation.unitCostCalculation === UNIT_COST_CALCULATION.PER_UNIT
          ? "Per Unit"
          : "Hourly",
    },
    {
      key: "cost",
      header: "Rate",
      width: 120,
      align: "right",
      format: (_value: unknown, operation: Operation) => (
        <OperationRowPrice operation={operation} variant="row" />
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      type: "date",
      width: 120,
      className: "hidden sm:table-cell",
    },
    {
      key: "syncStatus",
      header: "Sync",
      width: 90,
      type: "string",
      className: "hidden md:table-cell",
      format: (_value: unknown, operation: Operation) =>
        operation.externalMappings?.length ? (
          <Badge variant="secondary">Synced</Badge>
        ) : (
          "-"
        ),
    },
    {
      key: "actions",
      header: "",
      width: 50,
      format: (_value: unknown, operation: Operation) => {
        const loadingState = operationLoadingStates.get(operation.id);
        const isDeleting = loadingState?.isLoadingDelete;
        const isSynced = Boolean(operation.externalMappings?.length);

        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(operation);
            }}
            disabled={!canEditOperations || isSynced || isDeleting}
          >
            {isDeleting ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        );
      },
    },
  ];

  const handleCreate = () => {
    setIsCreateSheetOpen(true);
  };

  const handleEdit = (operation: Operation) => {
    setSelectedOperationId(operation.id);
    setIsEditSheetOpen(true);
  };

  const handleDelete = (operation: Operation) => {
    setOperationToDelete(operation);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveCreate = async (operation: Operation) => {
    setDisplayedOperationIds((prev) => [operation.id, ...prev]);
  };

  const handleImportComplete = useCallback(() => {
    fetchOperations({ includeArchived: showArchived }).then((result) =>
      setDisplayedOperationIds(result.ids),
    );
  }, [fetchOperations, setDisplayedOperationIds, showArchived]);

  const handleToggleArchived = (checked: boolean) => {
    setShowArchived(checked);
    fetchOperations({ includeArchived: checked }).then((result) =>
      setDisplayedOperationIds(result.ids),
    );
  };

  const handleArchived = (operationId: string) => {
    setIsEditSheetOpen(false);
    if (showArchived) {
      return;
    }
    setDisplayedOperationIds((prev) => prev.filter((id) => id !== operationId));
  };

  const handleConfirmDelete = async () => {
    if (!operationToDelete) {
      return;
    }

    const success = await deleteOperation(operationToDelete.id);
    if (success) {
      setDisplayedOperationIds((prev) =>
        prev.filter((id) => id !== operationToDelete.id),
      );
      toast.success("Operation deleted successfully");
    } else {
      toast.error("Failed to delete operation");
    }
    setIsDeleteDialogOpen(false);
    setOperationToDelete(null);
  };

  if (listError) {
    return (
      <div className="p-8 text-red-600">
        Error loading operations: {listError}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <DataTable
        columns={columns}
        data={operationsArray}
        onClickRow={(operation) => handleEdit(operation)}
        onChangeSearch={handleSearch}
        searchPlaceholder="Search operations..."
        className="h-full"
        isLoading={listLoading && operationsArray.length === 0}
        onCreate={canEditOperations ? handleCreate : undefined}
        createButtonText="New"
        toolbarActions={
          <div className="flex items-center gap-2">
            {canEditOperations && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsImportSheetOpen(true)}
              >
                <Upload className="h-4 w-4 mr-1" />
                Import
              </Button>
            )}
            <Switch
              id="show-archived-operations"
              checked={showArchived}
              onCheckedChange={handleToggleArchived}
            />
            <Label
              htmlFor="show-archived-operations"
              className="text-sm whitespace-nowrap"
            >
              Show Archived
            </Label>
          </div>
        }
      />

      <CsvImportSheet
        importType={CSV_IMPORT_TYPE.OPERATIONS}
        open={isImportSheetOpen}
        onOpenChange={setIsImportSheetOpen}
        onImportComplete={handleImportComplete}
      />

      {/* Create Operation Sheet */}
      <OperationEditorSheet
        open={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        onSave={handleSaveCreate}
      />

      {/* Edit Operation Sheet */}
      <OperationEditorSheet
        open={isEditSheetOpen}
        onOpenChange={setIsEditSheetOpen}
        operationId={selectedOperationId}
        onArchived={handleArchived}
        readOnly={!canEditOperations}
      />

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        entityType="Operation"
        itemName={operationToDelete?.name}
      />
    </div>
  );
}
